import { isAfter, isBefore, parseISO, subMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useRouter } from "next/router";
import { useMemo } from "react";
import toast from "react-hot-toast";
import useSortClientVisits from "@/components/serviceFlow/client/useSortClientVisits";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useCheckInAppointmentMutation } from "@/graphql/mutations/checkInAppointment.graphql.types";
import { useConfirmAppointmentMutation } from "@/graphql/mutations/confirmAppointment.graphql.types";
import {
  ClientByTokenQuery,
  useClientByTokenQuery,
} from "@/graphql/queries/clientByToken.graphql.types";
import useClientFormsByToken from "@/hooks/client/useClientFormsByToken";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import useAction from "@/hooks/misc/useAction";
import { VisitStatus } from "@/types";
import useErrorLogger from "@/utils/useErrorLogger";
import useUnsignedForms from "./useUnsignedForms";

type Visit =
  ClientByTokenQuery["clientAccessTokenByPk"]["client"]["visits"][number];

const MIN_TIME_TO_CHECK_IN = 30;

const canCheckInAppointment = (
  startTime: Date,
  medspaCurrentTime: Date
): boolean =>
  isAfter(medspaCurrentTime, subMinutes(startTime, MIN_TIME_TO_CHECK_IN)) &&
  isBefore(medspaCurrentTime, startTime);

export default function useOutstandingItems({
  medspaId,
  visits,
  forms,
}: {
  medspaId?: string;
  visits: Visit[];
  forms: ReturnType<typeof useClientFormsByToken>["forms"];
}) {
  const logError = useErrorLogger();
  const {
    query: { fromSMS },
  } = useRouter();
  const { clientAccessToken: token, getTokenContext } = useClientAccessToken();
  const { futureVisits } = useSortClientVisits(visits);
  const { checkInNotificationsV1Enabled } = useFeatureFlags();

  const { refetch: refetchClientForms } = useClientByTokenQuery({
    variables: {
      token: token,
    },
    ...getTokenContext(),
    skip: !token,
  });

  const [confirmAppointmentMutation] = useConfirmAppointmentMutation();

  const confirmAppointment = async (appointmentAccessToken: string) => {
    const mutateAndRefetch = async () => {
      await confirmAppointmentMutation({
        variables: {
          token: appointmentAccessToken,
          fromSms: fromSMS === "true",
        },
        ...getTokenContext(),
        update(cache) {
          cache.modify({
            fields: {
              appointmentPublicInfo(existingData) {
                return {
                  ...existingData,
                  status: VisitStatus.CONFIRMED,
                };
              },
            },
          });
        },
      });
      refetchClientForms();
    };

    try {
      await toast.promise(mutateAndRefetch(), {
        loading: "Confirming appointment...",
        success: "Appointment has been confirmed.",
        error: "Confirmation has failed.",
      });
    } catch (errors) {
      logError(errors);
    }
  };

  const {
    handleAction: confirmAppointmentAction,
    isLoading: isConfirmingAppointment,
  } = useAction(confirmAppointment);

  const toConfirm = futureVisits
    .filter(
      (visit) => visit.status === VisitStatus.SCHEDULED && visit.appointment
    )
    .map((visit) => visit.appointment);

  const { unsignedForms } = useUnsignedForms({ visits: futureVisits, forms });

  const { medspaTimezone } = useMedspaTimezone(medspaId);

  const toCheckIn = useMemo(() => {
    if (!medspaTimezone || !checkInNotificationsV1Enabled) {
      return [];
    }

    const medspaCurrentTime = utcToZonedTime(new Date(), medspaTimezone);

    return futureVisits
      .filter(
        (visit) =>
          (visit.status === VisitStatus.SCHEDULED ||
            visit.status === VisitStatus.CONFIRMED) &&
          visit.appointment &&
          canCheckInAppointment(
            parseISO(visit.appointment.startTime),
            medspaCurrentTime
          )
      )
      .map((visit) => visit.appointment);
  }, [medspaTimezone, futureVisits, checkInNotificationsV1Enabled]);

  const [checkInAppointmentMutation] = useCheckInAppointmentMutation();

  const checkInAppointment = async (appointmentAccessToken: string) => {
    const mutateAndRefetch = async () => {
      await checkInAppointmentMutation({
        variables: {
          token: appointmentAccessToken,
        },
        ...getTokenContext(),
        update(cache) {
          cache.modify({
            fields: {
              appointmentPublicInfo(existingData) {
                return {
                  ...existingData,
                  status: VisitStatus.CHECKED_IN,
                };
              },
            },
          });
        },
      });
      refetchClientForms();
    };

    try {
      await toast.promise(mutateAndRefetch(), {
        loading: "Checking in appointment...",
        success: "Appointment has been checked in.",
        error: "Check-in has failed.",
      });
    } catch (errors) {
      logError(errors);
    }
  };

  const {
    handleAction: checkInAppointmentAction,
    isLoading: isCheckingInAppointment,
  } = useAction(checkInAppointment);

  return {
    unsignedForms,
    toConfirm,
    confirmAppointmentAction,
    isConfirmingAppointment,
    token,
    toCheckIn,
    checkInAppointmentAction,
    isCheckingInAppointment,
  };
}
